import { FileDashedIcon } from "@phosphor-icons/react/dist/csr/FileDashed";
import { FunctionIcon } from "@phosphor-icons/react/dist/csr/Function";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import type { RunFigure } from "./types";

/**
 * In place of a file, for a figure no row of a file holds: one worked out
 * from the record's other figures (which are listed beside it, each with
 * its own files), a template setting (with the way to its Settings), or a
 * figure with no file this period.
 */
export const SourceEmpty = ({
  label,
  figure,
  input,
  settingsHref,
  onLeave,
}: {
  /** The figure's name: "Burden". */
  label: string;
  figure: RunFigure | null;
  /** What the template calls the input its column names, if any. */
  input: string | null;
  settingsHref: string;
  onLeave: () => void;
}) => {
  if (figure?.how === "formula")
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <FunctionIcon aria-hidden />
          </EmptyMedia>
          <EmptyTitle>Worked out from other figures</EmptyTitle>
          <EmptyDescription>
            {figure.note
              ? `${label} is ${figure.note}. Choose one of its figures to see the file it came from.`
              : `Choose one of ${label}'s figures to see the file it came from.`}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  if (figure?.how === "setting") {
    const name = figure.parts[0]?.note ?? figure.note ?? "an agent";
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <GearSixIcon aria-hidden />
          </EmptyMedia>
          <EmptyTitle>From the {name} setting</EmptyTitle>
          <EmptyDescription>
            No file holds it: it is set on the agent, and every period uses it.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<Link to={settingsHref} onClick={onLeave} />}
          >
            Open Settings
          </Button>
        </EmptyContent>
      </Empty>
    );
  }
  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <FileDashedIcon aria-hidden />
        </EmptyMedia>
        <EmptyTitle>No file to show</EmptyTitle>
        <EmptyDescription>
          {input
            ? `This period has no ${input} for this figure to come from.`
            : "This report doesn't trace this figure to a file yet."}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
};
